// Presence / status selector (P5) — shared constants + the idle-suppression epoch.
//
// Mirrors backend/disposition_constants.py PRESENCE_STATUSES. The status control lives in the
// admin header; the live status is server-persisted (GET/POST /api/dispositions/presence).
//
// IDLE SUPPRESSION — reuses the Phase 3.1c self-expiring cross-tab pattern (the same shape as
// TRAINING_ACTIVE_KEY in ActivityTrackerContext): while a non-Ready status is active, the
// selector writes a short, forward-dated epoch to localStorage and REFRESHES it on an interval.
// The (possibly different) leader tab reads presenceSuppressed() in its idle check and skips the
// popup. Because each write only buys PRESENCE_SUPPRESS_TTL_MS, a crashed/closed tab self-heals —
// the epoch lapses and normal idle resumes within the TTL. Bounded, self-healing, never a zombie.

export const PRESENCE_STATUSES = {
  ready: 'Ready',
  meeting: 'In a Meeting',
  break: 'On a Break',
  bathroom: 'In the Bathroom',
  // System-driven states (PP-3) — set automatically by the phone panel during a call, never pickable
  // by the rep. Shown in the one top-bar status next to the four manual states.
  on_call: 'On a Call',
  acw: 'After-Call Work',
};

export const PRESENCE_DEFAULT = 'ready';

// The rep can only manually pick these four; on_call / acw are set by the call lifecycle.
export const PRESENCE_SELECTABLE = ['ready', 'meeting', 'break', 'bathroom'];
export const PRESENCE_SYSTEM_STATES = ['on_call', 'acw'];
export const isSystemPresence = (status) => PRESENCE_SYSTEM_STATES.includes(status);

// Everything except Ready suppresses the idle popup (the declared status IS the answer).
export const isNonReady = (status) => !!status && status !== 'ready';

// Presentation: a small colored dot per status (R1/R2 palette; brand blue for Ready).
export const PRESENCE_DOT = {
  ready: 'bg-[#59a52c]',      // green — actively available
  meeting: 'bg-[#012697]',    // brand blue
  break: 'bg-amber-500',
  bathroom: 'bg-slate-400',
  on_call: 'bg-[#012697]',    // brand blue — live call
  acw: 'bg-amber-500',        // amber — wrapping up
};

// ── Cross-tab idle-suppression epoch (3.1c pattern) ──────────────────────────────
export const PRESENCE_SUPPRESS_KEY = 'tc_presence_active';
// Each refresh buys this much suppression; the refresh cadence must be shorter so it never lapses
// mid-status. Short enough that a dead tab self-heals quickly.
export const PRESENCE_SUPPRESS_TTL_MS = 90 * 1000;   // 90s bought per write
export const PRESENCE_SUPPRESS_REFRESH_MS = 45 * 1000; // rewrite every 45s while non-Ready

// True while ANY tab of this user has declared a non-Ready status recently (self-expiring).
export function presenceSuppressed() {
  try {
    return Date.now() < (Number(localStorage.getItem(PRESENCE_SUPPRESS_KEY)) || 0);
  } catch {
    return false;
  }
}

export function armPresenceSuppression() {
  try {
    localStorage.setItem(PRESENCE_SUPPRESS_KEY, String(Date.now() + PRESENCE_SUPPRESS_TTL_MS));
  } catch { /* ignore */ }
}

export function clearPresenceSuppression() {
  try {
    localStorage.removeItem(PRESENCE_SUPPRESS_KEY);
  } catch { /* ignore */ }
}

// ── Call-active idle suppression (PP-3) ───────────────────────────────────────────
// Same self-expiring cross-tab epoch as presenceSuppressed(), but armed by the call lifecycle
// (callSessionStore) while On a Call and during the FIRST N minutes of After-Call Work. On a Call +
// the first N minutes are working time and must never open an idle period. Reuses the 90s TTL / 45s
// refresh so a crashed tab self-heals.
export const CALL_SUPPRESS_KEY = 'tc_call_active';
export function callActiveSuppressed() {
  try {
    return Date.now() < (Number(localStorage.getItem(CALL_SUPPRESS_KEY)) || 0);
  } catch {
    return false;
  }
}
export function armCallSuppression() {
  try {
    localStorage.setItem(CALL_SUPPRESS_KEY, String(Date.now() + PRESENCE_SUPPRESS_TTL_MS));
  } catch { /* ignore */ }
}
export function clearCallSuppression() {
  try {
    localStorage.removeItem(CALL_SUPPRESS_KEY);
  } catch { /* ignore */ }
}

// ── After-Call-Work overage DEADLINE (PP-3 / R1.5) ─────────────────────────────────
// When After-Call Work BEGINS, the call lifecycle (beginAcw) writes the DEADLINE epoch here — the exact
// moment the overage will begin (acwStartedAt + the owner limit). Writing it up front (not at the limit)
// means the reason-required popup still fires even if the rep refreshes the page: the idle detector
// (ActivityTrackerContext), which is always mounted, reads this from localStorage, and once
// Date.now() >= the deadline opens EXACTLY ONE idle period from that moment through the EXISTING single
// idle writer (openIdlePeriod) — never a second writer. The detector clears it immediately so it fires
// once, and finish/Ready/new-call clear it so a rep who clicks Ready before the limit never triggers it.
export const ACW_OVERAGE_KEY = 'tc_acw_overage_since';
export function acwOverageSince() {
  try {
    const v = Number(localStorage.getItem(ACW_OVERAGE_KEY)) || 0;
    return v > 0 ? v : null;
  } catch {
    return null;
  }
}
export function setAcwOverageSince(epochMs) {
  try {
    localStorage.setItem(ACW_OVERAGE_KEY, String(epochMs));
  } catch { /* ignore */ }
}
// The ACW START epoch (when After-Call Work began). Persisted so the idle-period /start call can send
// acw_started_at and the SERVER re-derives the overage mark from the OWNER's acw_limit_minutes — it never
// trusts the client's claimed deadline (R2 idle clamp). Cleared together with the overage marker.
export const ACW_STARTED_KEY = 'tc_acw_started_since';
export function acwStartedSince() {
  try {
    const v = Number(localStorage.getItem(ACW_STARTED_KEY)) || 0;
    return v > 0 ? v : null;
  } catch {
    return null;
  }
}
export function setAcwStartedSince(epochMs) {
  try {
    localStorage.setItem(ACW_STARTED_KEY, String(epochMs));
  } catch { /* ignore */ }
}
export function clearAcwOverage() {
  try {
    localStorage.removeItem(ACW_OVERAGE_KEY);
    localStorage.removeItem(ACW_STARTED_KEY);
  } catch { /* ignore */ }
}
